import React, { useState, useEffect, useCallback } from 'react';
import MongoAtlasGuide from './MongoAtlasGuide';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BYODBPanel({ token, schoolId, language = 'hi' }) {
  const hi = language === 'hi';
  const [status, setStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [uri, setUri] = useState('');
  const [dbName, setDbName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/database`, { headers: authHeaders });
      const data = await res.json();
      setStatus(data);
      if (data.active) {
        try {
          const ures = await fetch(`${API_URL}/api/schools/${schoolId}/database/usage`, { headers: authHeaders });
          setUsage(await ures.json());
        } catch (e) { /* ignore */ }
      } else {
        setUsage(null);
      }
    } catch (e) {
      /* ignore */
    }
  }, [schoolId, token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!uri.trim()) { setResult({ success: false, message: hi ? 'Connection string डालें' : 'Enter connection string' }); return; }
    setBusy(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/database`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ mongo_uri: uri.trim(), mongo_db_name: dbName.trim() || null }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult({ success: true, message: data.message });
        setShowForm(false); setUri('');
        await load();
      } else {
        setResult({ success: false, message: data.detail || data.message || 'Failed' });
      }
    } catch (e) { setResult({ success: false, message: e.message }); }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/database/test`, { method: 'POST', headers: authHeaders });
      setResult(await res.json());
    } catch (e) { setResult({ success: false, message: e.message }); }
    setBusy(false);
  };

  const activate = async () => {
    if (!window.confirm(hi
      ? 'आपका मौजूदा students डेटा आपके अपने database में copy होगा और वहीं से चलेगा। जारी रखें?'
      : 'Your existing students data will be copied to your own database and served from there. Continue?')) return;
    setBusy(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/database/activate`, { method: 'POST', headers: authHeaders });
      const data = await res.json();
      setResult({ success: !!data.success, message: data.message || (data.success ? 'Activated' : 'Failed') });
      await load();
    } catch (e) { setResult({ success: false, message: e.message }); }
    setBusy(false);
  };

  const disconnect = async () => {
    if (!window.confirm(hi
      ? 'Database disconnect करने पर आपका डेटा वापस central पर copy होगा। जारी रखें?'
      : 'Disconnecting will copy your data back to central. Continue?')) return;
    setBusy(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/database`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      setResult({ success: !!data.success, message: data.message || 'Disconnected' });
      await load();
    } catch (e) { setResult({ success: false, message: e.message }); }
    setBusy(false);
  };

  const downloadBackup = async () => {
    setBusy(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/database/backup`, { headers: authHeaders });
      if (!res.ok) { setResult({ success: false, message: hi ? 'Backup बनाने में समस्या' : 'Backup failed' }); setBusy(false); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shiksha_mitra_backup_${schoolId}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setResult({ success: true, message: hi ? 'Backup download हो गया ✅' : 'Backup downloaded ✅' });
    } catch (e) { setResult({ success: false, message: e.message }); }
    setBusy(false);
  };

  if (!schoolId) return null;
  const configured = status?.configured;
  const active = status?.active;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="byodb-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-xl">🗄️</div>
          <div>
            <h3 className="font-semibold text-lg">{hi ? 'अपना Database (BYODB)' : 'Your Own Database (BYODB)'}</h3>
            <p className="text-sm text-gray-500">
              {hi ? 'अपना FREE MongoDB जोड़ें — "आपका डेटा आपके पास", platform की कोई cost नहीं'
                  : 'Connect your FREE MongoDB — "your data stays with you", zero platform cost'}
            </p>
          </div>
        </div>
        <span
          data-testid="byodb-status-badge"
          className={`px-3 py-1 rounded-full text-sm font-medium ${active ? 'bg-green-100 text-green-700' : configured ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
        >
          {active ? (hi ? 'Active ✅' : 'Active ✅') : configured ? (hi ? 'Connected (Not Active)' : 'Connected (Not Active)') : (hi ? 'Not Configured' : 'Not Configured')}
        </span>
      </div>

      {/* Capacity info card */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 mb-4" data-testid="byodb-capacity-card">
        {hi
          ? '💡 FREE MongoDB Atlas M0 (512MB) में एक सामान्य स्कूल (300–800 students) कई साल तक FREE चल सकता है। भरने पर एक क्लिक में upgrade — वो भी आपके अपने account पर।'
          : '💡 A typical school (300–800 students) runs for years FREE on MongoDB Atlas M0 (512MB). Upgrade in one click on your own account when it fills up.'}
      </div>

      {configured && (
        <div className="bg-gray-50 rounded-lg p-3 text-sm mb-3 space-y-1">
          <div><span className="text-gray-500">{hi ? 'Connection:' : 'Connection:'}</span> <span className="font-mono text-gray-700" data-testid="byodb-uri-preview">{status.uri_preview}</span></div>
          <div><span className="text-gray-500">DB:</span> <span className="font-mono text-gray-700">{status.db_name}</span></div>
          <div className="flex gap-4 pt-1">
            <span className="text-gray-500">{hi ? 'Central students:' : 'Central students:'} <b className="text-gray-700">{status.central_students}</b></span>
            <span className="text-gray-500">{hi ? 'आपके DB में:' : 'In your DB:'} <b className="text-gray-700">{status.school_students}</b></span>
          </div>
        </div>
      )}

      {/* Storage usage monitor (M0 512MB) */}
      {usage?.active && (
        <div className="mb-4" data-testid="byodb-usage">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>{hi ? 'Storage उपयोग (FREE M0)' : 'Storage used (FREE M0)'}</span>
            <span data-testid="byodb-usage-text">{usage.used_mb} MB / {usage.limit_mb} MB ({usage.percent}%)</span>
          </div>
          <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${usage.near_full ? 'bg-red-500' : usage.percent >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(usage.percent, 100)}%` }}
            />
          </div>
          {usage.near_full && (
            <div className="mt-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="byodb-upgrade-alert">
              {hi
                ? '⚠️ आपका FREE storage 80%+ भर चुका है। MongoDB Atlas में अपने cluster को M0 → M2/M5 पर upgrade करें (आपके अपने account पर), या पुराने passed-out बैच archive करें।'
                : '⚠️ Your FREE storage is 80%+ full. Upgrade your Atlas cluster M0 → M2/M5 (on your own account), or archive old passed-out batches.'}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {!showForm && (
        <div className="flex flex-wrap gap-2">
          {!configured && (
            <button onClick={() => { setShowForm(true); setResult(null); }} className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 shadow-sm" data-testid="btn-connect-db">
              + {hi ? 'अपना Database जोड़ें' : 'Connect your Database'}
            </button>
          )}
          {configured && !active && (
            <button onClick={activate} disabled={busy} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50" data-testid="btn-activate-db">
              {busy ? '...' : (hi ? '✅ Activate करें (डेटा migrate)' : '✅ Activate (migrate data)')}
            </button>
          )}
          {configured && (
            <>
              <button onClick={test} disabled={busy} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50" data-testid="btn-test-db">
                {hi ? 'Test Connection' : 'Test Connection'}
              </button>
              <button onClick={() => { setShowForm(true); setResult(null); }} className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200" data-testid="btn-edit-db">
                {hi ? 'बदलें' : 'Edit'}
              </button>
              <button onClick={disconnect} disabled={busy} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50" data-testid="btn-disconnect-db">
                {hi ? 'Disconnect' : 'Disconnect'}
              </button>
            </>
          )}
          <button onClick={() => setShowGuide(true)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" data-testid="btn-open-db-guide">
            {hi ? '📖 Setup Guide' : '📖 Setup Guide'}
          </button>
          <button onClick={downloadBackup} disabled={busy} className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 disabled:opacity-50" data-testid="btn-download-backup">
            {busy ? '...' : (hi ? '⬇️ Backup Download' : '⬇️ Download Backup')}
          </button>
        </div>
      )}

      {showForm && (
        <div className="mt-2 p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-3" data-testid="byodb-form">
          <h4 className="font-semibold text-emerald-900">{hi ? 'MongoDB Connection String डालें' : 'Enter MongoDB Connection String'}</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Connection String (mongodb+srv://...) *</label>
            <input
              type="text"
              value={uri}
              onChange={(e) => setUri(e.target.value)}
              placeholder="mongodb+srv://user:password@cluster0.xxxx.mongodb.net/"
              className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
              data-testid="input-db-uri"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{hi ? 'Database Name (optional)' : 'Database Name (optional)'}</label>
            <input
              type="text"
              value={dbName}
              onChange={(e) => setDbName(e.target.value)}
              placeholder="shiksha_mitra_school"
              className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
              data-testid="input-db-name"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50" data-testid="btn-save-db">
              {busy ? (hi ? 'जाँच रहे हैं...' : 'Validating...') : (hi ? 'Validate & Save' : 'Validate & Save')}
            </button>
            <button onClick={() => { setShowForm(false); setUri(''); setResult(null); }} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
            <button onClick={() => setShowGuide(true)} className="px-4 py-2.5 bg-white border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 ml-auto" data-testid="btn-open-db-guide-inline">
              {hi ? '📖 कैसे बनाएँ?' : '📖 How to create?'}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className={`mt-3 p-3 rounded-lg text-sm ${result.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="byodb-result">
          {result.success ? '✅ ' : '❌ '}{result.message}
        </div>
      )}

      {showGuide && <MongoAtlasGuide language={language} onClose={() => setShowGuide(false)} />}
    </div>
  );
}
